import React, { useState } from 'react';
import styled from '@emotion/styled';
import { BucketListItem } from '../types';

/**
 * Main bucket item container with soft, cute card styling based on status
 */
const BucketItemContainer = styled.div<{
  status: string;
  clickableCompleted?: boolean;
}>`
  background: ${props =>
    props.status === 'completed'
      ? 'linear-gradient(135deg, rgba(179, 255, 217, 0.6), rgba(144, 238, 144, 0.4))'
      : props.status === 'planned'
      ? 'linear-gradient(135deg, rgba(255, 202, 179, 0.6), rgba(255, 218, 185, 0.4))'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(255, 245, 230, 0.6))'};
  border: 2px solid
    ${props =>
      props.status === 'completed'
        ? 'rgba(144, 238, 144, 0.6)'
        : props.status === 'planned'
        ? 'rgba(255, 165, 0, 0.5)'
        : 'rgba(255, 179, 217, 0.4)'};
  border-radius: 20px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  box-shadow: 0 8px 25px rgba(255, 179, 217, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  position: relative;
  backdrop-filter: blur(10px);
  cursor: ${props =>
    props.clickableCompleted && props.status === 'completed'
      ? 'pointer'
      : 'default'};

  &:hover {
    transform: translateY(-5px) scale(1.02);
    box-shadow: 0 15px 35px rgba(255, 179, 217, 0.25),
      inset 0 1px 0 rgba(255, 255, 255, 0.8);
    border-color: ${props =>
      props.status === 'completed'
        ? 'rgba(144, 238, 144, 0.8)'
        : props.status === 'planned'
        ? 'rgba(255, 165, 0, 0.7)'
        : 'rgba(255, 105, 180, 0.6)'};
  }
`;

/**
 * Header container with flex layout for status, title, and reorder buttons
 */
const BucketItemHeader = styled.div<{
  status: string;
  readOnly?: boolean;
  hasChildren?: boolean;
  clickableCompleted?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: ${props => {
    if (props.readOnly) return 'default';
    if (props.hasChildren) return 'pointer';
    if (props.clickableCompleted && props.status === 'completed')
      return 'pointer';
    if (props.status === 'unplanned') return 'default';
    return 'default';
  }};
  border-radius: 12px;
  padding: 0.5rem;
  margin: -0.5rem;
  transition: background-color 0.2s ease;
`;

/**
 * Container for reorder buttons with cute styling
 */
const ReorderButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-left: auto;
`;

/**
 * Individual reorder button styling with soft, rounded aesthetic
 */
const ReorderButton = styled.button`
  width: 28px;
  height: 24px;
  background: linear-gradient(
    135deg,
    rgba(255, 179, 217, 0.6),
    rgba(212, 179, 255, 0.4)
  );
  border: 2px solid rgba(255, 105, 180, 0.3);
  border-radius: 12px;
  color: #8b5a8c;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  box-shadow: 0 3px 10px rgba(255, 105, 180, 0.2);

  &:hover:not(:disabled) {
    background: linear-gradient(
      135deg,
      rgba(255, 105, 180, 0.8),
      rgba(186, 85, 211, 0.6)
    );
    border-color: rgba(255, 20, 147, 0.6);
    transform: scale(1.15);
    color: white;
    box-shadow: 0 6px 20px rgba(255, 105, 180, 0.4);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
  }

  &:active:not(:disabled) {
    transform: scale(1.05);
  }
`;

/**
 * Status checkbox with cute, rounded styling and soft colors
 * Now also serves as expand/collapse button for items with children
 */
const StatusCheckbox = styled.div<{
  status: string;
  readOnly?: boolean;
  hasChildren?: boolean;
  clickableCompleted?: boolean;
}>`
  width: 26px;
  height: 26px;
  border: 3px solid
    ${props =>
      props.status === 'completed'
        ? '#90EE90'
        : props.status === 'planned'
        ? '#FFB347'
        : 'rgba(255, 179, 217, 0.6)'};
  border-radius: 50%;
  background: ${props =>
    props.status === 'completed'
      ? 'linear-gradient(135deg, #98FB98, #90EE90)'
      : props.status === 'planned'
      ? 'linear-gradient(135deg, #FFCAB0, #FFB347)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 245, 230, 0.7))'};
  cursor: default;
  opacity: ${props => (props.readOnly ? '0.8' : '1')};
  position: relative;
  flex-shrink: 0;
  transition: all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  box-shadow: 0 4px 12px rgba(255, 179, 217, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);

  &::after {
    content: ${props => {
      if (props.hasChildren) {
        return "'▶'"; // Will be rotated when expanded
      }
      return props.status === 'completed'
        ? "'✓'"
        : props.status === 'planned'
        ? "'~'"
        : "'○'";
    }};
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%)
      ${props => (props.hasChildren ? 'rotate(0deg)' : '')};
    font-size: ${props => (props.hasChildren ? '10px' : '14px')};
    line-height: 1;
    transition: transform 0.3s ease;
    font-weight: bold;
    color: ${props =>
      props.status === 'completed'
        ? '#2d5a2d'
        : props.status === 'planned'
        ? '#8b4513'
        : '#999'};
  }

  ${props =>
    props.hasChildren &&
    `
    &[data-expanded='true']::after {
      transform: translate(-50%, -50%) rotate(90deg);
    }
  `}
`;

/**
 * Bucket item title styling with cute font and conditional effects for completed items
 */
const BucketItemTitle = styled.h3<{ status: string }>`
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
  flex-grow: 1;
  font-family: 'Quicksand', sans-serif;
  color: #5d4e75;
  text-decoration: ${props =>
    props.status === 'completed' ? 'line-through' : 'none'};
  opacity: ${props => (props.status === 'completed' ? '0.7' : '1')};
  transition: all 0.3s ease;

  ${props =>
    props.status === 'completed' &&
    `
    background: linear-gradient(45deg, rgb(89 144 89), rgb(25 168 25)) text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  `}
`;

/**
 * Description styling with soft color and improved readability
 */
const BucketItemDescription = styled.p`
  margin: 0.5rem 0 0 0;
  color: #7a6b8a;
  font-size: 0.95rem;
  line-height: 1.5;
  font-weight: 400;
`;

/**
 * Container for badges/tokens - holds multiple badge elements
 */
const BadgeContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
  flex-wrap: wrap;
`;

/**
 * Individual badge/token component with cute styling
 * Variant types: 'memory' | 'photos' | 'story' | 'completed' | 'planned'
 */
const Badge = styled.div<{
  variant?: 'memory' | 'photos' | 'story' | 'completed' | 'planned' | 'info';
  isClickable?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.5rem 0.75rem;
  border-radius: 15px;
  font-size: 0.9rem;
  font-weight: 500;
  font-family: 'Quicksand', sans-serif;
  backdrop-filter: blur(10px);
  position: relative;
  cursor: ${props => (props.isClickable ? 'pointer' : 'default')};
  transition: ${props =>
    props.isClickable
      ? 'none'
      : 'all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)'};
  pointer-events: ${props => (props.isClickable ? 'none' : 'auto')};

  ${props => {
    switch (props.variant) {
      case 'memory':
        return `
          background: linear-gradient(135deg, rgba(179, 255, 217, 0.3), rgba(144, 238, 144, 0.2));
          border: 1px solid rgba(144, 238, 144, 0.4);
          color: #2d5a2d;
          box-shadow: 0 3px 10px rgba(179, 255, 217, 0.15);
        `;
      case 'photos':
        return `
          background: linear-gradient(135deg, rgba(179, 255, 217, 0.3), rgba(144, 238, 144, 0.2));
          border: 1px solid rgba(144, 238, 144, 0.4);
          color: #2d5a2d;
          box-shadow: 0 3px 10px rgba(179, 255, 217, 0.15);
        `;
      case 'story':
        return `
          background: linear-gradient(135deg, rgba(179, 255, 217, 0.3), rgba(144, 238, 144, 0.2));
          border: 1px solid rgba(144, 238, 144, 0.4);
          color: #2d5a2d;
          box-shadow: 0 3px 10px rgba(179, 255, 217, 0.15);
        `;
      case 'completed':
        return `
          background: linear-gradient(135deg, rgba(179, 255, 217, 0.3), rgba(144, 238, 144, 0.2));
          border: 1px solid rgba(144, 238, 144, 0.4);
          color: #2d5a2d;
          box-shadow: 0 3px 10px rgba(179, 255, 217, 0.15);
        `;
      case 'planned':
        return `
          background: linear-gradient(135deg, rgba(255, 202, 179, 0.3), rgba(255, 218, 185, 0.2));
          border: 1px solid rgba(255, 165, 0, 0.4);
          color: #d2691e;
          box-shadow: 0 3px 10px rgba(255, 202, 179, 0.15);
        `;
      default:
        return `
          background: linear-gradient(135deg, rgba(220, 220, 220, 0.6), rgba(240, 240, 240, 0.5));
          border: 1px solid rgba(200, 200, 200, 0.4);
          color: #666;
          box-shadow: 0 3px 10px rgba(200, 200, 200, 0.15);
        `;
    }
  }}
`;

/**
 * Container for child items with soft indentation and cute styling
 */
const ChildItems = styled.div<{ isExpanded: boolean }>`
  margin-left: 2.5rem;
  margin-top: 1.5rem;
  position: relative;
  max-height: ${props => (props.isExpanded ? '2000px' : '0')};
  overflow: ${props => (props.isExpanded ? 'visible' : 'hidden')};
  transition: max-height 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);

  &::before {
    content: '';
    position: absolute;
    left: -20px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(
      to bottom,
      rgba(255, 179, 217, 0.6),
      rgba(212, 179, 255, 0.4)
    );
    border-radius: 2px;
  }
`;

interface BucketItemProps {
  item: BucketListItem;
  children: BucketListItem[];
  readOnly?: boolean;
  onStatusChange?: (
    id: string,
    status: 'unplanned' | 'planned' | 'completed'
  ) => void;
  onReorder?: (id: string, direction: 'up' | 'down') => void;
  onCompletedClick?: (item: BucketListItem) => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
}

const BucketItemComponent: React.FC<BucketItemProps> = ({
  item,
  children,
  readOnly = false,
  onStatusChange,
  onReorder,
  onCompletedClick,
  canMoveUp = false,
  canMoveDown = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = children.length > 0;

  const handleStatusClick = () => {
    if (readOnly) return;

    // If item has children, toggle expand/collapse
    if (hasChildren) {
      setIsExpanded(!isExpanded);
      return;
    }

    // For other status changes (if we want to cycle through statuses)
    if (onStatusChange) {
      const statusOrder: ('unplanned' | 'planned' | 'completed')[] = [
        'unplanned',
        'planned',
        'completed',
      ];
      const currentIndex = statusOrder.indexOf(item.status);
      const nextIndex = (currentIndex + 1) % statusOrder.length;
      onStatusChange(item.id, statusOrder[nextIndex]);
    }
  };

  const handleContainerClick = () => {
    // If completed item is clicked anywhere, show modal
    if (item.status === 'completed' && onCompletedClick) {
      onCompletedClick(item);
    }
  };

  return (
    <BucketItemContainer
      status={item.status}
      clickableCompleted={!!onCompletedClick}
      onClick={
        item.status === 'completed' && onCompletedClick
          ? handleContainerClick
          : undefined
      }
    >
      <BucketItemHeader
        status={item.status}
        readOnly={readOnly}
        hasChildren={hasChildren}
        clickableCompleted={!!onCompletedClick}
        onClick={
          readOnly
            ? undefined
            : item.status === 'completed' && !hasChildren && onCompletedClick
            ? undefined // For completed items without children, no header click - let it bubble to container
            : e => {
                e.stopPropagation(); // Prevent container click for other cases
                handleStatusClick();
              }
        }
        title={
          readOnly
            ? `Status: ${item.status}`
            : hasChildren
            ? `Click to ${isExpanded ? 'collapse' : 'expand'}`
            : item.status === 'completed'
            ? 'Click to view completion details'
            : item.status === 'unplanned'
            ? 'Not clickable until planned or completed'
            : `Click to change status (currently: ${item.status})`
        }
      >
        <StatusCheckbox
          status={item.status}
          readOnly={readOnly}
          hasChildren={hasChildren}
          clickableCompleted={!!onCompletedClick}
          data-readonly={readOnly}
          data-expanded={isExpanded}
        />
        <BucketItemTitle status={item.status}>{item.title}</BucketItemTitle>
        {!readOnly && onReorder && (
          <ReorderButtons>
            <ReorderButton
              onClick={e => {
                e.stopPropagation();
                onReorder(item.id, 'up');
              }}
              disabled={!canMoveUp}
              title="Move up"
              aria-label="Move item up"
            >
              ↑
            </ReorderButton>
            <ReorderButton
              onClick={e => {
                e.stopPropagation();
                onReorder(item.id, 'down');
              }}
              disabled={!canMoveDown}
              title="Move down"
              aria-label="Move item down"
            >
              ↓
            </ReorderButton>
          </ReorderButtons>
        )}
      </BucketItemHeader>
      {item.description && (
        <BucketItemDescription>{item.description}</BucketItemDescription>
      )}
      {item.status === 'planned' && (
        <BadgeContainer>
          {item.plannedText && (
            <Badge variant="planned">📅 Planned: {item.plannedText}</Badge>
          )}
        </BadgeContainer>
      )}
      {item.status === 'completed' && (
        <BadgeContainer>
          {item.completedAt && (
            <Badge variant="completed">✓ Completed: {item.completedAt}</Badge>
          )}
          <Badge variant="memory" isClickable>
            📖 View memory
          </Badge>
          {item.images && item.images.length > 0 && (
            <Badge variant="photos">
              📷 {item.images.length}{' '}
              {item.images.length === 1 ? 'photo' : 'photos'}
            </Badge>
          )}
          {item.blogText && <Badge variant="story">📝 Story</Badge>}
        </BadgeContainer>
      )}
      {children.length > 0 && (
        <ChildItems isExpanded={isExpanded}>
          {children.map((child, index) => (
            <BucketItemComponent
              key={child.id}
              item={child}
              children={[]} // Assuming we only support one level of nesting for now
              readOnly={readOnly}
              onStatusChange={onStatusChange}
              onReorder={onReorder}
              onCompletedClick={onCompletedClick}
              canMoveUp={index > 0}
              canMoveDown={index < children.length - 1}
            />
          ))}
        </ChildItems>
      )}
    </BucketItemContainer>
  );
};

export default BucketItemComponent;
